<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import ItemFilter from "./ItemFilter.vue";
import { useVesselItemStore } from "@/stores/management/item-vessel";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Vessel Item",
  url: "/management/vessel-item/create",
};

const headers: Header[] = [
  { text: "MSG CODE", value: "msg_code" },
  { text: "IMPA CODE", value: "impa_code" },
  { text: "ITEM NAME", value: "goods_name" },
  { text: "VESSEL", value: "vessel_name" },
  { text: "MAX QUANTITY", value: "quantity" },
  { text: "ROB", value: "rob" },
  { text: "ACTIONS", value: "action" },
];

const vesselItemStore = useVesselItemStore();
const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listVesselItems = ref<any>([]);
const isLoading = ref(false);

const loadDataVesselItems = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await vesselItemStore.dispatchGetList(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listVesselItems.value = await vesselItemStore.vesselItems.items;
    page.value = await vesselItemStore.vesselItems.page;
    limit.value = await vesselItemStore.vesselItems.pageSize;
    totalItems.value = await vesselItemStore.vesselItems.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataVesselItems();
};

const handleEdit = (item: any) => {
  router.push("/management/vessel-item/" + item);
};

const handleDelete = async (item: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${item.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await vesselItemStore.dispatchDelete(
          item.id
        );
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDataVesselItems();
        } else {
          toast.error(message);
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadDataVesselItems();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Data Vessel Item</h3>
        <div class="w-full" v-if="linkAdd != null">
          <a
            class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg"
            :href="linkAdd?.url"
          >
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>{{ linkAdd?.text }}</span>
            </span>
          </a>
        </div>
      </div>
      <ItemFilter :link="linkAdd" @searchByQuery="loadDataVesselItems" />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listVesselItems"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-image="props">
            <img :src="props.image_link" alt="image-link" class="image-item" />
          </template>
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('vessel_item_quantity-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('vessel_item_quantity-delete')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
<style>
.image-item {
  width: 200px;
  height: auto;
  object-fit: contain;
  padding: 10px;
}
</style>
